export interface RouteSeoMetadata {
  title: string
  description: string
  url: string
  type?: 'website' | 'article'
}

interface RouteRule {
  match: (pathname: string) => boolean
  metadata: Omit<RouteSeoMetadata, 'url'>
}

const BASE_URL = 'https://lukhas.ai'

const ROUTE_RULES: RouteRule[] = [
  // ───────────────────────────────────────────────
  // Experience
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/experience/coming-soon',
    metadata: {
      title: 'LUKHAS Experience — Coming Soon',
      description:
        'LUKHAS Experience adapts how digital content is delivered according to user preference, context, accessibility, and audience. Register your interest.',
    },
  },
  {
    match: (pathname) => pathname === '/experience',
    metadata: {
      title: 'LUKHAS Experience',
      description:
        'Explore the LUKHAS Experience platform — adaptive content delivery shaped by user preference, context, and accessibility.',
    },
  },
  {
    match: (pathname) => pathname === '/dream-experience',
    metadata: {
      title: 'Dream Experience',
      description:
        'An immersive journey through the LUKHAS Dream module — creative synthesis and oneiric processing visualised in real time.',
    },
  },
  {
    match: (pathname) => pathname === '/purple-experience',
    metadata: {
      title: 'Purple Experience',
      description:
        'The LUKHAS Purple Experience — a cinematic exploration of consciousness-inspired interface design.',
    },
  },

  // ───────────────────────────────────────────────
  // Homepage
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/',
    metadata: {
      title: 'LUKHAS AI - Consciousness-Aware Intelligence',
      description:
        'LUKHAS AI builds auditable reasoning systems with governance, drift detection, and evidence-first product experiences.',
    },
  },

  // ───────────────────────────────────────────────
  // Public pages
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/about',
    metadata: {
      title: 'About LUKHAS AI',
      description:
        'Learn how LUKHAS AI approaches consciousness-inspired system design through traceable architecture, governance, and product discipline.',
    },
  },
  {
    match: (pathname) => pathname === '/brand',
    metadata: {
      title: 'Brand',
      description:
        'The LUKHAS AI brand identity — logo, colour system, typography, and visual language guidelines.',
    },
  },
  {
    match: (pathname) => pathname === '/technology',
    metadata: {
      title: 'Technology',
      description:
        'Explore the Constellation Framework, MATRIZ pipeline, and the system mechanics behind LUKHAS AI.',
    },
  },
  {
    match: (pathname) => pathname === '/pricing',
    metadata: {
      title: 'Pricing',
      description:
        'See current LUKHAS AI pricing tiers, product surfaces, and enterprise access options.',
    },
  },
  {
    match: (pathname) => pathname === '/use-cases',
    metadata: {
      title: 'Use Cases',
      description:
        'Discover how organisations apply LUKHAS AI across governance, creative, healthcare, finance, and other domains.',
    },
  },
  {
    match: (pathname) => pathname === '/faq',
    metadata: {
      title: 'FAQ',
      description:
        'Frequently asked questions about LUKHAS AI products, architecture, pricing, and deployment.',
    },
  },
  {
    match: (pathname) => pathname === '/proof',
    metadata: {
      title: 'Proof',
      description:
        'Inspect live benchmark, governance, accessibility, and identity evidence behind LUKHAS AI product claims.',
    },
  },
  {
    match: (pathname) => pathname === '/vision',
    metadata: {
      title: 'Vision',
      description:
        'The long-term vision behind LUKHAS AI — from consciousness-inspired architecture to auditable, adaptive intelligence.',
    },
  },
  {
    match: (pathname) => pathname === '/matriz',
    metadata: {
      title: 'MATRIZ Pipeline',
      description:
        'Understand the MATRIZ processing pipeline — the multi-stage reasoning backbone of LUKHAS AI systems.',
    },
  },
  {
    match: (pathname) => pathname === '/orchestration',
    metadata: {
      title: 'Orchestration',
      description:
        'How LUKHAS AI orchestrates reasoning across Constellation stars, drift detection, and recovery mechanisms.',
    },
  },
  {
    match: (pathname) => pathname === '/knowledge',
    metadata: {
      title: 'Knowledge',
      description:
        'Browse the LUKHAS AI knowledge base — system documentation, research papers, and architectural references.',
    },
  },
  {
    match: (pathname) => pathname === '/showcase',
    metadata: {
      title: 'Showcase',
      description:
        'Visual showcase of LUKHAS AI interfaces, product surfaces, and design system components.',
    },
  },
  {
    match: (pathname) => pathname === '/transparency',
    metadata: {
      title: 'Transparency',
      description:
        'Transparency reports, audit trails, and governance disclosures for LUKHAS AI systems.',
    },
  },
  {
    match: (pathname) => pathname === '/capabilities',
    metadata: {
      title: 'Capabilities Report',
      description:
        'Current capability status, benchmark results, and system maturity indicators for LUKHAS AI.',
    },
  },
  {
    match: (pathname) => pathname === '/timeline',
    metadata: {
      title: 'Timeline',
      description:
        'The LUKHAS AI development timeline — milestones, research phases, and product evolution.',
    },
  },
  {
    match: (pathname) => pathname === '/qrg',
    metadata: {
      title: 'QRG — Quantum Resistant Glyph',
      description:
        'Explore the Quantum Resistant Glyph — a post-quantum cryptographic identity primitive developed by LUKHAS AI.',
    },
  },
  {
    match: (pathname) => pathname === '/playground',
    metadata: {
      title: 'Playground',
      description:
        'Test LUKHAS AI models interactively — experiment with reasoning, consciousness, and governance APIs in real time.',
    },
  },

  // ───────────────────────────────────────────────
  // Auth pages
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/login',
    metadata: {
      title: 'Log In',
      description:
        'Sign in to your LUKHAS AI account to access Studio, API keys, and workspace settings.',
    },
  },
  {
    match: (pathname) => pathname === '/signup',
    metadata: {
      title: 'Sign Up',
      description:
        'Create a LUKHAS AI account to access Studio, APIs, and consciousness-aware product surfaces.',
    },
  },
  {
    match: (pathname) => pathname === '/request-access',
    metadata: {
      title: 'Request Access',
      description:
        'Request early access to the LUKHAS AI platform, Studio workspace, and API programme.',
    },
  },
  {
    match: (pathname) => pathname === '/access-pending',
    metadata: {
      title: 'Access Pending',
      description:
        'Your LUKHAS AI access request is under review. You will be notified once approved.',
    },
  },
  {
    match: (pathname) => pathname === '/sso/callback',
    metadata: {
      title: 'SSO Callback',
      description:
        'Completing single sign-on authentication for LUKHAS AI.',
    },
  },
  {
    match: (pathname) => pathname === '/portal',
    metadata: {
      title: 'Portal',
      description:
        'Controlled-access entry point for the LUKHAS AI platform.',
    },
  },

  // ───────────────────────────────────────────────
  // Research pages (specific before catch-all)
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/research/vivox',
    metadata: {
      title: 'Research — Vivox',
      description:
        'Vivox research: real-time voice and communication primitives for consciousness-aware multi-agent systems.',
      type: 'article',
    },
  },
  {
    match: (pathname) => pathname === '/research/dream-module',
    metadata: {
      title: 'Research — Dream Module',
      description:
        'Dream module research: oneiric code generation, creative synthesis, and parallel training in LUKHAS AI.',
      type: 'article',
    },
  },
  {
    match: (pathname) => pathname === '/research/guardian',
    metadata: {
      title: 'Research — Guardian',
      description:
        'Guardian system research: CollapseHash, drift detection, kill switches, and governance safeguards in LUKHAS AI.',
      type: 'article',
    },
  },
  {
    match: (pathname) => pathname === '/research/cognitive-mesh',
    metadata: {
      title: 'Research — Cognitive Mesh',
      description:
        'Cognitive Mesh research: distributed reasoning networks and inter-star communication in the Constellation Framework.',
      type: 'article',
    },
  },
  {
    match: (pathname) => pathname === '/research/memory-healix',
    metadata: {
      title: 'Research — Memory Healix',
      description:
        'Memory Healix research: self-repairing memory structures with fold-based integrity verification.',
      type: 'article',
    },
  },
  {
    match: (pathname) => pathname === '/research/quantum-bio-fusion',
    metadata: {
      title: 'Research — Quantum-Bio Fusion',
      description:
        'Quantum-Bio Fusion research: quantum-inspired computation combined with bio-inspired adaptation in LUKHAS AI.',
      type: 'article',
    },
  },
  {
    match: (pathname) => pathname === '/research/swarm-consciousness',
    metadata: {
      title: 'Research — Swarm Consciousness',
      description:
        'Swarm Consciousness research: emergent collective behaviour and multi-agent coordination in LUKHAS AI systems.',
      type: 'article',
    },
  },
  {
    match: (pathname) => pathname === '/research' || pathname.startsWith('/research/'),
    metadata: {
      title: 'Research',
      description:
        'Read the research track for LUKHAS AI systems, including Guardian, Dream, Memory, and related architectural studies.',
    },
  },

  // ───────────────────────────────────────────────
  // Product pages
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/products/nias',
    metadata: {
      title: 'NIAS — Neuro-Inspired Authentication',
      description:
        'NIAS provides consent-aware, neuro-inspired authentication and identity management for LUKHAS AI applications.',
    },
  },
  {
    match: (pathname) => pathname === '/products/dast',
    metadata: {
      title: 'DAST — Dynamic Adaptive Security Testing',
      description:
        'DAST delivers continuous adaptive security testing with governance-aware vulnerability analysis for LUKHAS AI.',
    },
  },
  {
    match: (pathname) => pathname === '/products/seedra',
    metadata: {
      title: 'SEEDRA',
      description:
        'SEEDRA — a seed-stage reasoning accelerator for bootstrapping consciousness-aware intelligence pipelines.',
    },
  },
  {
    match: (pathname) => pathname === '/products/abas',
    metadata: {
      title: 'ABAS — Adaptive Behavioural Analysis',
      description:
        'ABAS monitors behavioural patterns with drift detection and adaptive response across LUKHAS AI systems.',
    },
  },

  // ───────────────────────────────────────────────
  // Solutions — Capability Pillars
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/solutions/agents',
    metadata: {
      title: 'Solutions — Agents',
      description:
        'Deploy autonomous agents with built-in governance, drift detection, and auditable decision trails using LUKHAS AI.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/insights',
    metadata: {
      title: 'Solutions — Insights',
      description:
        'Extract structured insights from unstructured data with consciousness-aware reasoning and traceable outputs.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/creative',
    metadata: {
      title: 'Solutions — Creative',
      description:
        'Augment creative workflows with Dream-powered generation, style governance, and adaptive content pipelines.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/governance',
    metadata: {
      title: 'Solutions — Governance',
      description:
        'Implement auditable AI governance with policy enforcement, compliance tracking, and real-time drift alerts.',
    },
  },

  // ───────────────────────────────────────────────
  // Solutions — Industry Verticals
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/solutions/healthcare',
    metadata: {
      title: 'Solutions — Healthcare',
      description:
        'LUKHAS AI for healthcare: governance-first reasoning, consent-aware data handling, and clinical decision support.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/finance',
    metadata: {
      title: 'Solutions — Finance',
      description:
        'LUKHAS AI for finance: auditable risk analysis, compliance automation, and drift-aware trading intelligence.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/education',
    metadata: {
      title: 'Solutions — Education',
      description:
        'LUKHAS AI for education: adaptive learning systems, accessibility-first content, and traceable assessment pipelines.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/creative-industries',
    metadata: {
      title: 'Solutions — Creative Industries',
      description:
        'LUKHAS AI for creative industries: Dream-powered content generation, style governance, and IP-aware workflows.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/insurance',
    metadata: {
      title: 'Solutions — Insurance',
      description:
        'LUKHAS AI for insurance: automated claims reasoning, fraud detection, and governance-compliant underwriting.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/transport',
    metadata: {
      title: 'Solutions — Transport',
      description:
        'LUKHAS AI for transport: real-time route optimisation, safety monitoring, and adaptive logistics intelligence.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/fashion-retail',
    metadata: {
      title: 'Solutions — Fashion & Retail',
      description:
        'LUKHAS AI for fashion and retail: trend analysis, personalised recommendations, and supply chain intelligence.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/hospitality',
    metadata: {
      title: 'Solutions — Hospitality',
      description:
        'LUKHAS AI for hospitality: guest experience personalisation, demand forecasting, and operational intelligence.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/public-sector',
    metadata: {
      title: 'Solutions — Public Sector',
      description:
        'LUKHAS AI for the public sector: transparent decision-making, citizen service automation, and policy compliance.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/grocery-retail',
    metadata: {
      title: 'Solutions — Grocery Retail',
      description:
        'LUKHAS AI for grocery retail: inventory optimisation, waste reduction, and personalised shopping intelligence.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/gig-logistics',
    metadata: {
      title: 'Solutions — Gig & Logistics',
      description:
        'LUKHAS AI for gig economy and logistics: dynamic dispatch, driver safety, and governance-aware fleet management.',
    },
  },
  {
    match: (pathname) => pathname === '/solutions/aviation',
    metadata: {
      title: 'Solutions — Aviation',
      description:
        'LUKHAS AI for aviation: predictive maintenance, flight operations intelligence, and safety-critical governance.',
    },
  },

  // ───────────────────────────────────────────────
  // Enterprise
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/enterprise',
    metadata: {
      title: 'Enterprise',
      description:
        'Review enterprise deployment patterns for LUKHAS AI with governance, compliance, and observability built in.',
    },
  },

  // ───────────────────────────────────────────────
  // Constellation
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/constellation' || pathname.startsWith('/constellation/'),
    metadata: {
      title: 'Constellation Framework',
      description:
        'Browse the eight cognitive stars of the LUKHAS AI Constellation Framework and how they connect into a traceable system.',
    },
  },

  // ───────────────────────────────────────────────
  // Docs (catch-all — specific docs routes inherit this)
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/docs' || pathname.startsWith('/docs'),
    metadata: {
      title: 'Documentation',
      description:
        'Developer documentation for the LUKHAS AI platform, APIs, SDKs, and Constellation Framework concepts.',
    },
  },

  // ───────────────────────────────────────────────
  // Blog & Podcast
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/podcast' || pathname.startsWith('/podcast/'),
    metadata: {
      title: 'Podcast — Living Intelligence',
      description:
        'Living Intelligence: The Story of LUKHAS. Ten episodes tracing the journey from mitochondrial biology to consciousness-inspired AI.',
      type: 'article',
    },
  },
  {
    match: (pathname) => pathname === '/blog' || pathname.startsWith('/blog/'),
    metadata: {
      title: 'Blog',
      description:
        'Articles, product notes, and technical writing from the LUKHAS AI team.',
      type: 'article',
    },
  },

  // ───────────────────────────────────────────────
  // Community & Support
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/careers' || pathname.startsWith('/careers/'),
    metadata: {
      title: 'Careers',
      description:
        'Open roles and hiring information for teams building LUKHAS AI products and infrastructure.',
    },
  },
  {
    match: (pathname) => pathname === '/contact',
    metadata: {
      title: 'Contact',
      description:
        'Contact LUKHAS AI for enterprise, research, partnership, or support conversations.',
    },
  },
  {
    match: (pathname) => pathname === '/contribute',
    metadata: {
      title: 'Contribute',
      description:
        'Contribute to LUKHAS AI — open-source participation, research collaboration, and community guidelines.',
    },
  },
  {
    match: (pathname) => pathname === '/support',
    metadata: {
      title: 'Support',
      description:
        'Get help with LUKHAS AI products, APIs, billing, and account issues.',
    },
  },
  {
    match: (pathname) => pathname === '/events',
    metadata: {
      title: 'Events',
      description:
        'Upcoming and past LUKHAS AI events, talks, workshops, and community meetups.',
    },
  },
  {
    match: (pathname) => pathname === '/newsletter',
    metadata: {
      title: 'Newsletter',
      description:
        'Subscribe to the LUKHAS AI newsletter for product updates, research highlights, and community news.',
    },
  },

  // ───────────────────────────────────────────────
  // Chat
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/chat',
    metadata: {
      title: 'Chat',
      description:
        'Interactive chat experience for exploring LUKHAS AI capabilities and product surfaces.',
    },
  },

  // ───────────────────────────────────────────────
  // Legal pages
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/privacy',
    metadata: {
      title: 'Privacy Policy',
      description:
        'Read the LUKHAS AI privacy policy, data handling commitments, and user rights information.',
    },
  },
  {
    match: (pathname) => pathname === '/terms',
    metadata: {
      title: 'Terms of Service',
      description:
        'Review the terms governing use of LUKHAS AI services, products, and APIs.',
    },
  },
  {
    match: (pathname) => pathname === '/cookies',
    metadata: {
      title: 'Cookie Policy',
      description:
        'Understand how LUKHAS AI uses cookies, essential preferences, and consent controls.',
    },
  },
  {
    match: (pathname) => pathname === '/compliance',
    metadata: {
      title: 'Compliance',
      description:
        'Compliance posture, controls, and governance practices across LUKHAS AI systems.',
    },
  },
  {
    match: (pathname) => pathname === '/safety',
    metadata: {
      title: 'Safety',
      description:
        'Safety model, escalation paths, drift detection, and governance controls used by LUKHAS AI.',
    },
  },

  // ───────────────────────────────────────────────
  // Settings
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/settings',
    metadata: {
      title: 'Settings',
      description:
        'Manage your LUKHAS AI display preferences, background, and visual settings.',
    },
  },

  // ───────────────────────────────────────────────
  // Studio (specific app routes before catch-all)
  // ───────────────────────────────────────────────
  {
    match: (pathname) => pathname === '/studio/app/chat',
    metadata: {
      title: 'Studio — Chat',
      description: 'LUKHAS AI Studio chat workspace.',
    },
  },
  {
    match: (pathname) => pathname === '/studio/app/keys',
    metadata: {
      title: 'Studio — API Keys',
      description: 'Manage your LUKHAS AI API keys and access tokens.',
    },
  },
  {
    match: (pathname) => pathname === '/studio/app/secrets',
    metadata: {
      title: 'Studio — Secrets',
      description: 'Manage encrypted secrets for LUKHAS AI integrations.',
    },
  },
  {
    match: (pathname) => pathname === '/studio/app/settings',
    metadata: {
      title: 'Studio — Settings',
      description: 'Configure your LUKHAS AI Studio workspace settings.',
    },
  },
  {
    match: (pathname) => pathname === '/studio/app/billing',
    metadata: {
      title: 'Studio — Billing',
      description: 'View and manage your LUKHAS AI billing and payment details.',
    },
  },
  {
    match: (pathname) => pathname === '/studio/app/usage',
    metadata: {
      title: 'Studio — Usage',
      description: 'Monitor your LUKHAS AI API usage and quota consumption.',
    },
  },
  {
    match: (pathname) => pathname === '/studio/app/upgrade',
    metadata: {
      title: 'Studio — Upgrade',
      description: 'Upgrade your LUKHAS AI plan for higher limits and additional features.',
    },
  },
  {
    match: (pathname) => pathname === '/studio/app/security',
    metadata: {
      title: 'Studio — Security',
      description: 'Manage passkeys, authentication methods, and security settings.',
    },
  },
  {
    match: (pathname) => pathname === '/studio/app/preferences',
    metadata: {
      title: 'Studio — Preferences',
      description: 'Customise your LUKHAS AI Studio experience and display preferences.',
    },
  },
  {
    match: (pathname) => pathname === '/studio/app/connectors',
    metadata: {
      title: 'Studio — Connectors',
      description: 'Connect external services and data sources to your LUKHAS AI workspace.',
    },
  },
  {
    match: (pathname) => pathname === '/studio/app/qrg-playground',
    metadata: {
      title: 'Studio — QRG Playground',
      description: 'Experiment with Quantum Resistant Glyph encoding in the Studio sandbox.',
    },
  },
  {
    match: (pathname) => pathname === '/studio' || pathname.startsWith('/studio/'),
    metadata: {
      title: 'Studio',
      description:
        'LUKHAS AI Studio for API keys, chat, usage, connectors, and workspace configuration.',
    },
  },
]

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') {
    return '/'
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

export function getSeoMetadata(pathname: string): RouteSeoMetadata {
  const normalizedPath = normalizePathname(pathname)
  const matchedRoute = ROUTE_RULES.find((route) => route.match(normalizedPath))

  if (matchedRoute) {
    return {
      ...matchedRoute.metadata,
      url: `${BASE_URL}${normalizedPath}`,
    }
  }

  return {
    title: 'LUKHAS AI',
    description:
      'LUKHAS AI builds consciousness-inspired systems with auditable reasoning, governance, and traceable interfaces.',
    url: `${BASE_URL}${normalizedPath}`,
  }
}
